/**
 * BLOQUE 3 — Pestaña "Escenario y Línea de Tiempo".
 * Se auto-dispara cuando entras a la pestaña y aún no existe scenario.
 * Usa el job en background del Bloque 0 (POST run-analysis kind=scenario),
 * polling cada 4s al endpoint analysis-status/{job_id}.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { Loader2, AlertTriangle, CheckCircle, RotateCcw } from "lucide-react";

const CLASS_COLORS = {
  NORMAL: { bg: "rgba(0,200,83,0.10)", border: "#00C853", text: "#00C853" },
  ADVERTENCIA: { bg: "rgba(251,140,0,0.12)", border: "#FB8C00", text: "#FB8C00" },
  CRITICO: { bg: "rgba(213,0,0,0.14)", border: "#D50000", text: "#D50000" },
};
const PLAUS_COLOR = { Alto: "#00C853", Medio: "#FB8C00", Bajo: "#90A4AE" };

export default function ScenarioTab({ c, reload, onNavigate }) {
  const scenario = c?.investigation?.scenario;
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [selectedHyp, setSelectedHyp] = useState(() =>
    new Set((scenario?.hypotheses || []).map((h) => h.id).filter(Boolean))
  );
  const pollRef = useRef(null);

  // Iniciar análisis automáticamente si no hay scenario
  const start = useCallback(async () => {
    setError("");
    setProgress({ status: "processing", progress_pct: 0, current_step: "Iniciando..." });
    try {
      const r = await api.post(`/investigations/${c.id}/run-analysis`, { kind: "scenario" });
      setJobId(r.data.job_id);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setProgress(null);
    }
  }, [c.id]);

  // Auto-trigger en mount si no hay scenario y hay evidencia
  useEffect(() => {
    if (!scenario && !jobId && !progress) {
      const hasEv = (c?.evidence?.files || []).length > 0;
      if (hasEv) start();
    }
  }, [scenario, jobId, progress, c?.evidence?.files, start]);

  // Polling
  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    async function poll() {
      try {
        const r = await api.get(`/investigations/${c.id}/analysis-status/${jobId}`);
        if (!mounted) return;
        setProgress(r.data);
        if (r.data.status === "completed") {
          await reload();
          setJobId(null);
        } else if (r.data.status === "failed") {
          setError(r.data.error || "El análisis falló");
          setJobId(null);
        }
      } catch (e) {
        setError(e?.message);
        setJobId(null);
      }
    }
    poll();
    pollRef.current = setInterval(poll, 4000);
    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId, c.id, reload]);

  function toggleHyp(id) {
    setSelectedHyp((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const timeline = scenario?.timeline || [];
  const hypotheses = scenario?.hypotheses || [];
  const running = jobId && progress && progress.status === "processing";

  return (
    <div className="space-y-3" data-testid="psc-scenario-tab">
      {!scenario && !running && !error && (
        <div className="card text-center" style={{ padding: 24 }}>
          <div className="text-sm text-dim mb-2">
            Sube evidencias en la pestaña anterior. El análisis del escenario
            se construirá automáticamente.
          </div>
          {(c?.evidence?.files || []).length > 0 && (
            <button className="btn btn-primary" onClick={start} data-testid="psc-scenario-start">
              Generar Escenario Ahora
            </button>
          )}
        </div>
      )}

      {running && (
        <div className="card" data-testid="psc-scenario-progress">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 size={16} className="spin"/>
            <b>Construyendo escenario con IA...</b>
            <span className="text-xs text-dim">{progress.progress_pct}%</span>
          </div>
          <div style={{ background: "var(--bg-2)", borderRadius: 4, height: 8, overflow: "hidden" }}>
            <div style={{
              width: `${progress.progress_pct || 0}%`,
              height: "100%", background: "var(--accent)",
              transition: "width 0.4s ease",
            }}/>
          </div>
          <div className="text-xs text-dim mt-2">{progress.current_step}</div>
        </div>
      )}

      {error && (
        <div className="card" style={{ background: "rgba(213,0,0,0.10)", borderColor: "#D50000" }}>
          <div className="flex items-center justify-between">
            <div className="text-sm" data-testid="psc-scenario-error"><AlertTriangle size={12} style={{display:"inline"}}/> {error}</div>
            <button className="btn btn-sm" onClick={start} data-testid="psc-scenario-retry">
              <RotateCcw size={11}/> Reintentar
            </button>
          </div>
        </div>
      )}

      {scenario && timeline.length > 0 && (
        <div className="card" data-testid="psc-scenario-timeline">
          <div className="card-title">🕒 Línea de Tiempo Factual ({timeline.length} eventos)</div>
          <div className="text-xs text-dim mb-2">
            Generado: {scenario.generated_at || ""}
          </div>
          <div style={{ position: "relative", paddingLeft: 16 }}>
            {timeline.map((ev, i) => {
              const cls = CLASS_COLORS[ev.classification] || CLASS_COLORS.NORMAL;
              return (
                <div key={`tl-${i}`} className="mb-2" style={{
                  borderLeft: `3px solid ${cls.border}`,
                  background: cls.bg, padding: "10px 12px",
                  borderRadius: "0 6px 6px 0",
                }} data-testid={`psc-tl-${i}`}>
                  <div className="flex items-center gap-2">
                    <span style={{
                      background: cls.text, color: "#FFF", padding: "2px 8px",
                      borderRadius: 10, fontSize: 10, fontWeight: 700,
                    }}>{ev.classification || "NORMAL"}</span>
                    <span className="text-xs text-dim">{ev.timestamp || "—"}</span>
                  </div>
                  <div className="text-sm mt-1"><b>{ev.event}</b></div>
                  {ev.environment && <div className="text-xs text-dim mt-1">🌐 {ev.environment}</div>}
                  {ev.people && <div className="text-xs text-dim">👥 {ev.people}</div>}
                  {ev.equipment_state && <div className="text-xs text-dim">⚙ {ev.equipment_state}</div>}
                  {ev.quote && <div className="text-xs mt-1" style={{ fontStyle: "italic" }}>&ldquo;{ev.quote}&rdquo;</div>}
                  {ev.evidence_ref && (
                    <div className="text-xs text-dim mt-1">📎 {ev.evidence_ref}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {scenario && hypotheses.length > 0 && (
        <div className="card" data-testid="psc-scenario-hypotheses">
          <div className="card-title">💡 Hipótesis de Escenario ({hypotheses.length})</div>
          <div className="text-xs text-dim mb-2">
            Marca las hipótesis que el equipo considere plausibles. Estas se enviarán
            como contexto al análisis de metodologías.
          </div>
          <div className="space-y-2">
            {hypotheses.map((h) => {
              const id = h.id || h.title;
              const checked = selectedHyp.has(id);
              const pc = PLAUS_COLOR[h.plausibility] || "#90A4AE";
              return (
                <div key={`hy-${id}`} className="card" style={{
                  borderLeft: `3px solid ${pc}`,
                  background: checked ? "rgba(21,101,192,0.08)" : "var(--bg-2)",
                }} data-testid={`psc-hyp-${id}`}>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox" checked={checked}
                      onChange={() => toggleHyp(id)}
                      data-testid={`psc-hyp-check-${id}`}
                      style={{ marginTop: 4 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div className="flex items-center gap-2 mb-1">
                        <b>HIPÓTESIS {id}: {h.title}</b>
                        <span style={{
                          background: pc, color: "#FFF",
                          padding: "2px 8px", borderRadius: 10,
                          fontSize: 10, fontWeight: 700,
                        }}>{h.plausibility || "Medio"}</span>
                      </div>
                      <div className="text-sm" style={{ marginBottom: 6 }}>{h.narrative}</div>
                      {(h.supporting_variables || []).length > 0 && (
                        <div className="text-xs"><b style={{color:"#00C853"}}>✓ A favor:</b> {h.supporting_variables.join(" · ")}</div>
                      )}
                      {(h.weakening_variables || []).length > 0 && (
                        <div className="text-xs"><b style={{color:"#D50000"}}>✗ En contra:</b> {h.weakening_variables.join(" · ")}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-3 items-center">
            <div className="text-xs text-dim">{selectedHyp.size} hipótesis seleccionada(s)</div>
            <div className="flex gap-2">
              <button className="btn btn-sm" onClick={start} data-testid="psc-scenario-regen">
                <RotateCcw size={11}/> Regenerar Escenario
              </button>
              <button
                className="btn btn-primary"
                onClick={() => onNavigate && onNavigate("investigation", { selectedHypotheses: [...selectedHyp] })}
                disabled={selectedHyp.size === 0}
                data-testid="psc-scenario-continue"
              >
                <CheckCircle size={11}/> Continuar a Investigación IA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
